// @ts-strict-ignore
import { activateFloorAdhesionOnUAP, SlotSetupDefinition } from '@platforms/shared';
import {
	AdSlotClass,
	communicationService,
	context,
	CookieStorageAdapter,
	InstantConfigService,
	OpenWeb,
	RepeatableSlotPlaceholderConfig,
	SlotPlaceholderConfig,
	slotPlaceholderInjector,
	slotService,
	UapLoadStatus,
	utils,
} from '@wikia/ad-engine';
import { Injectable } from '@wikia/dependency-injection';
import { AD_ENGINE_UAP_LOAD_STATUS } from '../../../../communication/events/events-ad-engine-uap';
import { UcpMobileTopBoxadMultipleIcbExperiment } from '../experiments/ucp-mobile-top-boxad-multiple-icb-experiment';

// eslint-disable-next-line no-restricted-imports
import {
	isRoadblockV2Enabled,
	isUapOrRoadblockV2,
} from '../../../../core/utils/condition/roadblock-slots-visibility';

const logGroup = 'ucp-mobile-slots-definition-repository';

@Injectable()
export class UcpMobileSlotsDefinitionRepository {
	constructor(
		protected instantConfig: InstantConfigService,
		private openWeb: OpenWeb,
		private ucpMobileTopBoxadMultipleIcbExperiment: UcpMobileTopBoxadMultipleIcbExperiment,
	) {}

	getTopLeaderboardConfig(): SlotSetupDefinition {
		if (!this.isTopLeaderboardApplicable()) {
			return;
		}

		// If mobileMoreIcbExperiment is active, don't setup TLB - placeholder will be removed server-side
		if (context.get('experiments.mobileMoreIcb')) {
			const experimentLogGroup = 'UcpMobileMoreIcbExperiment';
			utils.logger(experimentLogGroup, 'More ICB experiment is enabled, disable TLB');
			return;
		}

		const slotName = 'top_leaderboard';
		const placeholderConfig = context.get(`slots.${slotName}.placeholder`);
		const activator = () => {
			context.push('state.adStack', { id: slotName });
		};

		return {
			activator,
			slotCreatorWrapperConfig: null,
			slotCreatorConfig: {
				slotName,
				placeholderConfig,
				anchorSelector: '.top-leaderboard',
				insertMethod: 'prepend',
				classList: [AdSlotClass.HIDDEN_AD_CLASS, 'ad-slot'],
			},
		};
	}

	private isTopLeaderboardApplicable(): boolean {
		const pageType = context.get('wiki.targeting.pageType');
		const hasFeaturedVideo = context.get('custom.hasFeaturedVideo');
		const hasPageHeader = !!document.querySelector('.wiki-page-header');
		const hasPortableInfobox = !!document.querySelector('.portable-infobox');

		return pageType !== 'special' || hasPortableInfobox || (hasPageHeader && !hasFeaturedVideo);
	}

	getGalleryLeaderboardConfig(): SlotSetupDefinition {
		const slotName = 'gallery_leaderboard';
		const placeholderConfig = context.get(`slots.${slotName}.placeholder`);

		return {
			slotCreatorConfig: {
				slotName,
				placeholderConfig,
				anchorSelector: '.gallery-leaderboard',
				insertMethod: 'prepend',
				classList: ['ad-slot'],
			},
			activator: () => {
				context.push('state.adStack', { id: slotName });
			},
		};
	}

	getTopBoxadConfig(): SlotSetupDefinition {
		if (!this.isInContentApplicable()) {
			return;
		}

		const slotName = 'top_boxad';

		const config = this.ucpMobileTopBoxadMultipleIcbExperiment.getConfig();
		utils.logger(logGroup, 'Top Boxad config', config);

		return {
			slotCreatorConfig: {
				slotName,
				placeholderConfig: {
					createLabel: true,
				},
				anchorSelector: config.anchorSelector,
				insertMethod: config.insertMethod,
				classList: [AdSlotClass.HIDDEN_AD_CLASS, 'ad-slot'],
				avoidConflictWith: ['.ntv-ad'],
			},
			slotCreatorWrapperConfig: {
				classList: ['ad-slot-placeholder', 'top-boxad', 'is-loading'],
			},
			activator: () => {
				this.pushWaitingSlot(slotName);
				communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
					if (isUapOrRoadblockV2(action, action.isLoaded)) {
						this.injectIncontentAdsPlaceholders(1);
						return;
					}
					this.injectIncontentAdsPlaceholders(this.isIncontentPlayerApplicable() ? 21 : 20);
				});
			},
		};
	}

	getIncontentBoxadConfig(): SlotSetupDefinition {
		if (!this.isInContentApplicable()) {
			return;
		}

		const slotNamePrefix = 'incontent_boxad_';
		const slotName = `${slotNamePrefix}1`;

		return {
			slotCreatorConfig: {
				slotName,
				anchorSelector: '.incontent-boxad',
				avoidConflictWith: ['.ad-slot'],
				insertMethod: 'append',
				classList: [AdSlotClass.HIDDEN_AD_CLASS, 'ad-slot'],
				repeat: {
					index: 1,
					limit: 20,
					slotNamePattern: `${slotNamePrefix}{slotConfig.repeat.index}`,
					updateProperties: {
						adProduct: '{slotConfig.slotName}',
						'targeting.rv': '{slotConfig.repeat.index}',
						'targeting.pos': ['incontent_boxad'],
						bidBeforeRun: false,
					},
				},
			},
			activator: () => {
				if (isRoadblockV2Enabled()) {
					communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, () => {
						context.push('events.pushOnScroll.ids', slotName);
					});
				} else {
					context.push('events.pushOnScroll.ids', slotName);
				}
			},
		};
	}

	private getMoreIcbExperimentPlacements(): string {
		const contentWrapper = document.querySelector('.mw-parser-output');
		const theParagraphs = Array.from(contentWrapper.getElementsByTagName('p'));
		const minCharacterCount = context.get('experiments.mobileTopBoxadMultipleIcbCount')
			? context.get('experiments.mobileTopBoxadMultipleIcbCount')
			: 2800; // Character count between each ICB, change this value (ADEN-14313)
		const topBoxad = document.getElementsByClassName('top-boxad')[0] as HTMLElement;
		let [currentLength, pAnchors, tmpParaIndex, tmpSectionIndex, lastSectionId] = [0, '', 0, 0, ''];

		theParagraphs.forEach((value) => {
			const [pOffset, pLength, pParentEl] = [
				value.offsetTop,
				value.textContent.length,
				value.parentElement,
			];

			// If paragraph is before .top-boxad location, ignore it
			if (topBoxad && pOffset < topBoxad?.offsetTop) {
				return;
			}

			currentLength += pLength;
			if (pParentEl.nodeName == 'SECTION' && lastSectionId != pParentEl.id) {
				tmpSectionIndex = 0;
			}
			if (pParentEl.nodeName == 'SECTION') {
				tmpSectionIndex++;
				if (currentLength >= minCharacterCount) {
					pAnchors += `.mw-parser-output section#${value.parentElement.id} p:nth-of-type(${tmpSectionIndex}),`;
					currentLength = 0;
				}
			} else {
				tmpParaIndex++;
				// Find the nth paragraph (where n = tmpParaIndex variable) in the content
				// that is a possible candidate for ICB insertion, and make sure that it is after the top_boxad.
				// We don't want an ICB unit inserted before the top_boxad. This needs to be kept consistent.
				const paragraph = document.querySelector(
					`.mw-parser-output p:nth-of-type(${tmpParaIndex})`,
				) as HTMLElement | null;
				// Check if the candidate paragraph is past the top_boxad
				const isParagraphPastTopBoxAd = paragraph && paragraph?.offsetTop > topBoxad?.offsetTop;
				// Check if the last ICB unit is past the minimum character count threshold and if the paragraph is past the top_boxad
				if (currentLength >= minCharacterCount && isParagraphPastTopBoxAd) {
					// Add the selector to the list of ICB placements
					pAnchors += `.mw-parser-output p:nth-of-type(${tmpParaIndex}),`;
					// Reset the character count for the next ICB unit
					currentLength = 0;
				}
			}
			lastSectionId = pParentEl.id;
		});

		if (pAnchors) {
			pAnchors = pAnchors.replace(/,\s*$/, '');
		}

		utils.logger(logGroup, 'More ICB experiment placements', pAnchors);

		return pAnchors;
	}

	private isInContentApplicable(): boolean {
		const pageType = context.get('wiki.targeting.pageType');

		if (pageType === 'home') {
			return !!document.querySelector('.curated-content');
		}

		return pageType !== 'search';
	}

	private injectIncontentAdsPlaceholders(count: number): void {
		const adSlotCategory = 'incontent';

		let anchorsToSelect = '';
		if (
			context.get('experiments.mobileMoreIcb') ||
			context.get('experiments.mobileTopBoxadMultipleIcb')
		) {
			anchorsToSelect =
				this.getMoreIcbExperimentPlacements() || context.get('templates.incontentAnchorSelector');
		} else {
			anchorsToSelect = context.get('templates.incontentAnchorSelector');
		}

		const icbPlaceholderConfig: RepeatableSlotPlaceholderConfig = {
			classList: ['ad-slot-placeholder', 'incontent-boxad', 'is-loading'],
			anchorSelector: anchorsToSelect,
			insertMethod: 'before',
			avoidConflictWith: [
				'top-leaderboard',
				'.top-boxad',
				'.bottom-leaderboard',
				'.incontent-boxad',
				'.openweb-slot',
			],
			repeatStart: 1,
			repeatLimit: count,
			...(context.get('options.removeOpenWebAddICB')
				? {}
				: { repeatExceptions: [this.buildOpenWebReplacement()] }),
		};

		slotPlaceholderInjector.injectAndRepeat(icbPlaceholderConfig, adSlotCategory);
	}

	private buildOpenWebReplacement(): (repeat: number) => SlotPlaceholderConfig | null {
		const newConfigOverride: SlotPlaceholderConfig = <SlotPlaceholderConfig>{
			classList: ['openweb-slot'],
			anchorSelector: context.get('templates.incontentAnchorSelector'),
			insertMethod: 'before',
			noLabel: true,
		};

		return (repeat) => {
			return repeat === OpenWeb.MOBILE_REPLACE_REPEAT_SLOT_IDX && this.openWeb.isActive()
				? newConfigOverride
				: null;
		};
	}

	getMobilePrefooterConfig(): SlotSetupDefinition {
		if (!this.isMobilePrefooterApplicable()) {
			return;
		}

		const slotName = 'mobile_prefooter';

		return {
			slotCreatorConfig: {
				slotName,
				placeholderConfig: {
					createLabel: true,
				},
				anchorSelector: '.global-footer',
				insertMethod: 'before',
				classList: [AdSlotClass.HIDDEN_AD_CLASS, 'ad-slot'],
			},
			slotCreatorWrapperConfig: {
				classList: [
					'ad-slot-placeholder',
					'mobile-prefooter',
					'is-loading',
					AdSlotClass.HIDDEN_AD_CLASS,
				],
			},
			activator: () => {
				communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
					if (action.isLoaded) {
						this.pushWaitingSlot(slotName);

						const mobilePrefooter = document.querySelector('.mobile-prefooter');
						mobilePrefooter.classList.remove(AdSlotClass.HIDDEN_AD_CLASS);
					}
				});
			},
		};
	}

	private isMobilePrefooterApplicable(): boolean {
		const MIN_NUMBER_OF_SECTIONS = 4;

		if (context.get('wiki.targeting.pageType') === 'home') {
			return !!document.querySelector('.trending-articles');
		}

		const numberOfSections = document.querySelectorAll('.mw-parser-output > h2').length;
		const hasArticleFooter = !!document.querySelector('.article-footer');

		return (
			(hasArticleFooter && !this.isInContentApplicable()) ||
			numberOfSections > MIN_NUMBER_OF_SECTIONS
		);
	}

	getBottomLeaderboardConfig(): SlotSetupDefinition {
		if (!this.isBottomLeaderboardApplicable()) {
			return;
		}

		const slotName = 'bottom_leaderboard';

		return {
			slotCreatorConfig: {
				slotName,
				placeholderConfig: {
					createLabel: true,
				},
				anchorSelector: '.bottom-leaderboard',
				insertMethod: 'prepend',
				classList: [AdSlotClass.HIDDEN_AD_CLASS, 'ad-slot'],
			},
			slotCreatorWrapperConfig: null,
			activator: () => {
				if (context.get('options.lazyLoadBottomLeaderboard')) {
					utils.logger(
						logGroup,
						`pushWaitingSlot with forcedLazyLoading for ${slotName} before uap started.`,
					);
					this.pushWaitingSlot(slotName, true);
				} else {
					this.pushWaitingSlot(slotName);
				}
			},
		};
	}

	private isBottomLeaderboardApplicable(): boolean {
		return (
			!!document.querySelector('.global-footer') &&
			context.get('wiki.targeting.pageType') !== 'search'
		);
	}

	getIncontentPlayerConfig(): SlotSetupDefinition {
		if (!this.isIncontentPlayerApplicable()) {
			return;
		}

		const slotName = 'incontent_player';
		const controlledSectionId = document
			.querySelector('.mw-parser-output > h2:first-of-type')
			?.getAttribute('aria-controls');

		return {
			slotCreatorConfig: {
				slotName,
				anchorSelector: `#${controlledSectionId}`,
				anchorPosition: 'firstViable',
				insertMethod: 'prepend',
				avoidConflictWith: [],
			},
			activator: () => {
				context.push('events.pushOnScroll.ids', slotName);
			},
		};
	}

	private isIncontentPlayerApplicable(): boolean {
		const LOG_GROUP = 'isIncontentPlayerApplicable';
		const isNoSecondaryVideoExperimentEnabled = context.get('experiments.noSecondaryVideo');

		if (isNoSecondaryVideoExperimentEnabled) {
			utils.logger(LOG_GROUP, 'disabling IncontentPlayer due to an experiment');
			return false;
		}

		return (
			context.get('custom.hasIncontentPlayer') && context.get('wiki.targeting.pageType') !== 'home'
		);
	}

	getDrawerPlayerConfig(): SlotSetupDefinition {
		if (!this.isIncontentPlayerApplicable()) {
			return;
		}

		const slotName = 'drawer';
		return {
			slotCreatorConfig: {
				slotName,
				anchorSelector: '#popularVideosPlayer',
				anchorPosition: 'firstViable',
				insertMethod: 'prepend',
				avoidConflictWith: [],
			},
			activator: () => {
				context.push('state.adStack', { id: slotName });
			},
		};
	}

	getFloorAdhesionConfig(): SlotSetupDefinition {
		const LOG_GROUP = 'UcpMobileGetFloorAdhesionConfig';

		const isPersistentMobileAdhesionExperiment = context.get(
			'experiments.persistentAdhesionExperiment',
		);

		let slotPushed = false;
		let adhesionFooterSpaceApplied = false;

		const slotName = isPersistentMobileAdhesionExperiment ? 'adhesion_limited' : 'floor_adhesion';
		utils.logger(
			LOG_GROUP,
			'isPersistentMobileAdhesionExperiment: ',
			isPersistentMobileAdhesionExperiment,
		);

		const activateFloorAdhesion = () => {
			// When the persistent mobile floor adhesion unit is activated and loaded on the mobile wiki page,
			// let's add 60px of height to the global footer current height to prevent the adhesion unit from overlapping the footer.
			// This will allow the View Full Site button to be visible, since the persistent adhesion unit cannot be closed.
			if (isPersistentMobileAdhesionExperiment && !adhesionFooterSpaceApplied) {
				utils.logger(
					LOG_GROUP,
					'activateFloorAdhesion - isPersistentMobileAdhesionExperiment: ',
					isPersistentMobileAdhesionExperiment,
				);

				const ucpMobileFooter = document.querySelector('.global-footer__bottom') as HTMLElement;
				// Safety check, just in case.
				if (ucpMobileFooter) {
					const currentFooterHeight = ucpMobileFooter.getBoundingClientRect()?.height;
					ucpMobileFooter.style.height = `${currentFooterHeight + 60}px`;
					adhesionFooterSpaceApplied = true;
					utils.logger(
						LOG_GROUP,
						'activateFloorAdhesion - adhesionFooterSpaceApplied to ',
						ucpMobileFooter,
						': ',
						adhesionFooterSpaceApplied,
					);
				}
			} else {
				utils.logger(
					LOG_GROUP,
					'activateFloorAdhesion - footer height was not applied. Reason - isPersistentMobileAdhesionExperiment: ',
					isPersistentMobileAdhesionExperiment,
					' | !adhesionFooterSpaceApplied: ',
					!adhesionFooterSpaceApplied,
				);
			}

			if (slotPushed) {
				utils.logger(LOG_GROUP, 'activateFloorAdhesion - Floor adhesion already activated');
				return;
			}

			slotService.pushSlotById(slotName);

			slotPushed = true;
		};

		return {
			slotCreatorConfig: {
				slotName,
				anchorSelector: '#floor_adhesion_container',
				insertMethod: 'append',
				classList: [AdSlotClass.HIDDEN_AD_CLASS, 'ad-slot'],
			},
			activator: () => activateFloorAdhesionOnUAP(activateFloorAdhesion, false),
		};
	}

	getInterstitialConfig(): SlotSetupDefinition {
		if (!this.isInterstitialApplicable()) {
			return;
		}

		const slotName = 'interstitial';

		return {
			slotCreatorConfig: {
				slotName,
				anchorSelector: '#fandom-mobile-wrapper',
				insertMethod: 'after',
				classList: [AdSlotClass.HIDDEN_AD_CLASS, 'ad-slot'],
			},
			activator: () => {
				this.pushWaitingSlot(slotName);
			},
		};
	}

	private isInterstitialApplicable(): boolean {
		const cookieAdapter = new CookieStorageAdapter();

		return this.instantConfig.get('icInterstitial') && !cookieAdapter.getItem('_ae_intrsttl_imp');
	}

	private pushWaitingSlot(slotName: string, forceLazyLoad?: boolean): void {
		communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
			if (action.isLoaded || forceLazyLoad) {
				if (forceLazyLoad) {
					utils.logger(
						logGroup,
						`pushWaitingSlot with forcedLazyLoading for ${slotName} after uap started.`,
					);
				}
				context.push('events.pushOnScroll.ids', slotName);
			} else {
				slotService.pushSlotById(slotName);
			}
		});
	}
}
